import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { apiClient } from '../lib/api-client';

const SUPPORTED_LANGUAGES = [
  'en',
  'ja',
  'es',
  'fr',
  'ko',
  'de',
  'pt',
  'tr',
  'zh-cn',
  'zh-tw',
  'it'
];

function CreateTierPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    key: '',
    name: '',
    displayName: Object.fromEntries(SUPPORTED_LANGUAGES.map(lang => [lang, ''])),
    description: Object.fromEntries(SUPPORTED_LANGUAGES.map(lang => [lang, ''])),
    displayOrder: 0,
    isActive: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.key || !formData.name) {
      setError('Key and Name are required');
      return;
    }

    if (!formData.displayName.en) {
      setError('Display Name (EN) is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.createTier({
        key: formData.key.toUpperCase(),
        name: formData.name,
        displayName: formData.displayName,
        description: Object.values(formData.description).some(v => v) ? formData.description : null,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive
      });

      navigate('/tiers');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='max-w-4xl space-y-6'>
      <div className='flex items-center gap-4'>
        <Link
          to='/tiers'
          className='text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
        >
          <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M10 19l-7-7m0 0l7-7m-7 7h18'
            />
          </svg>
        </Link>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Create New Tier</h1>
      </div>

      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <p className='text-red-800 dark:text-red-200'>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='space-y-8 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'
      >
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {/* Key */}
          <div>
            <label className='block text-sm font-semibold text-gray-700 dark:text-gray-300'>
              Key <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={formData.key}
              onChange={e => setFormData(prev => ({ ...prev, key: e.target.value.toUpperCase() }))}
              placeholder='e.g. PREMIUM'
              className='mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400'
              required
            />
            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
              Alphanumeric and underscores only. Automatically converted to uppercase.
            </p>
          </div>

          {/* Internal Name */}
          <div>
            <label className='block text-sm font-semibold text-gray-700 dark:text-gray-300'>
              Internal Name <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder='e.g. Premium'
              className='mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400'
              required
            />
          </div>
        </div>

        {/* Display Name (multi-language) */}
        <div>
          <label className='mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300'>
            Display Name <span className='text-red-500'>*</span>
          </label>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {SUPPORTED_LANGUAGES.map(lang => (
              <div key={lang}>
                <label className='block text-xs text-gray-500 dark:text-gray-400'>
                  {lang.toUpperCase()}
                  {lang === 'en' ? ' *' : ''}
                </label>
                <input
                  type='text'
                  value={formData.displayName[lang] ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      displayName: { ...prev.displayName, [lang]: e.target.value }
                    }))
                  }
                  className='mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400'
                  required={lang === 'en'}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Description (multi-language) */}
        <div>
          <label className='mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300'>
            Description (Optional)
          </label>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {SUPPORTED_LANGUAGES.map(lang => (
              <div key={lang}>
                <label className='block text-xs text-gray-500 dark:text-gray-400'>
                  {lang.toUpperCase()}
                </label>
                <input
                  type='text'
                  value={formData.description[lang] ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      description: { ...prev.description, [lang]: e.target.value }
                    }))
                  }
                  className='mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400'
                />
              </div>
            ))}
          </div>
        </div>

        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {/* Display Order */}
          <div>
            <label className='block text-sm font-semibold text-gray-700 dark:text-gray-300'>
              Display Order
            </label>
            <input
              type='number'
              value={formData.displayOrder}
              onChange={e =>
                setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))
              }
              className='mt-1 block w-32 rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400'
            />
          </div>

          {/* Active Toggle */}
          <div className='flex items-center'>
            <label className='flex cursor-pointer items-center gap-3'>
              <div className='relative'>
                <input
                  type='checkbox'
                  checked={formData.isActive}
                  onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className='sr-only'
                />
                <div
                  className={`h-6 w-11 rounded-full transition-colors ${formData.isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                ></div>
                <div
                  className={`absolute top-1 left-1 h-4 w-4 transform rounded-full bg-white transition-transform ${formData.isActive ? 'translate-x-5' : ''}`}
                ></div>
              </div>
              <span className='text-sm font-semibold text-gray-700 dark:text-gray-300'>Active</span>
            </label>
          </div>
        </div>

        <div className='flex items-center gap-4 pt-4'>
          <button
            type='submit'
            disabled={loading}
            className='rounded-md bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-700 dark:hover:bg-blue-600 dark:focus:ring-offset-gray-900'
          >
            {loading ? 'Creating...' : 'Create Tier'}
          </button>
          <Link
            to='/tiers'
            className='rounded-md bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

export default CreateTierPage;
